import { zodResolver } from "@hookform/resolvers/zod";
import { Head, router, usePage } from "@inertiajs/react";
import {
	CreditCard,
	Dot,
	FileText,
	Info,
	LoaderIcon,
	PackageIcon,
	Plus,
	Tag,
	Trash2,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import JsonDescEditor from "@/components/admin-portal/service/json-desc-editor";
import {
	FormPageContainer,
	FormPageHeader,
} from "@/components/admin-portal/shared/page-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { CommandGroup, CommandSeparator } from "@/components/ui/command";
import { Input as InputExtended } from "@/components/ui/extended/input";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	MultiSelector,
	MultiSelectorContent,
	MultiSelectorInput,
	MultiSelectorItem,
	MultiSelectorList,
	MultiSelectorTrigger,
} from "@/components/ui/multi-select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { useIsMobile } from "@/hooks/use-mobile";
import { groupLocationsByCountry } from "@/lib/locations";
import { goBackHandler } from "@/lib/utils";
import type { Location } from "@/types/admin-portal/location";
import type { Service } from "@/types/admin-portal/service";
import type { GlobalPageProps } from "@/types/globals";

export interface EditPageProps {
	service: Service;
	locations: Location[];
	packages: [];
}

const PackageSummaryCard = ({
	fieldIndex,
	form,
	currency,
}: {
	fieldIndex: number;
	form: any;
	currency: string;
}) => {
	const { t } = useTranslation("brands");
	const watchValues = useWatch({
		control: form.control,
		name: `packages.${fieldIndex}`,
	});

	const {
		numberOfVisit,
		numberOfFreeVisit,
		pricePerVisit,
		discount,
		feePerVisit,
	} = watchValues || {};

	const totalVisits = Number(numberOfVisit) || 0;
	const freeVisits = Number(numberOfFreeVisit) || 0;
	const paidVisits = Math.max(0, totalVisits - freeVisits);
	const price = Number(pricePerVisit) || 0;
	const fee = Number(feePerVisit) || 0;
	const disc = Number(discount) || 0;

	const totalPrice = paidVisits * price - disc;
	const totalFee = paidVisits * fee;

	const formatCurrency = (val: number) => {
		return new Intl.NumberFormat("id-ID", {
			style: "currency",
			currency: currency || "IDR",
			minimumFractionDigits: 0,
		}).format(val);
	};

	return (
		<Card className="bg-card border shadow-none">
			<CardHeader>
				<CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
					{t("edit.summary.title")}
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				<div className="flex justify-between text-sm">
					<span className="text-muted-foreground">
						{t("edit.summary.paid_visits")}
					</span>
					<span className="font-medium">
						{t("edit.summary.visits_ratio", {
							paid: paidVisits,
							total: totalVisits,
						})}
					</span>
				</div>
				<Separator className="bg-border/50" />
				<div className="flex justify-between text-sm">
					<span className="text-muted-foreground">
						{t("edit.summary.total_price")}
					</span>
					<span className="font-bold text-primary">
						{formatCurrency(totalPrice)}
					</span>
				</div>
				<div className="flex justify-between text-sm">
					<span className="text-muted-foreground">
						{t("edit.summary.total_fee")}
					</span>
					<span className="font-medium">{formatCurrency(totalFee)}</span>
				</div>
				{freeVisits > 0 && (
					<div className="mt-2 rounded-sm bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
						{t("edit.summary.complimentary", { count: freeVisits })}
					</div>
				)}
			</CardContent>
		</Card>
	);
};

const PackageDescriptionEditor = ({
	fieldIndex,
	form,
}: {
	fieldIndex: number;
	form: any;
}) => {
	const jsonDesc = useWatch({
		control: form.control,
		name: `packages.${fieldIndex}.jsonDesc`,
	});

	const handleChange = useCallback(
		(newVal: Record<string, unknown> | null) => {
			form.setValue(`packages.${fieldIndex}.jsonDesc`, newVal);
		},
		[form, fieldIndex],
	);

	return <JsonDescEditor value={jsonDesc ?? null} onChange={handleChange} />;
};

export default function Edit({ service, locations }: EditPageProps) {
	const { props: globalProps } = usePage<
		GlobalPageProps & {
			errors: Record<keyof Service, string[]> & { fullMessages: string[] };
		}
	>();
	const { t } = useTranslation("brands");
	const isMobile = useIsMobile();
	// SERVICE_FEE_ENABLE: off, the backend bills neither fee and ignores both values sent.
	const serviceFeeEnabled =
		globalProps.adminPortal?.featureFlags?.serviceFeeEnabled ?? false;
	const groupedLocations = useMemo(
		() => groupLocationsByCountry(locations),
		[locations],
	);

	// form state management
	const [activeTab, setActiveTab] = useState("brand-info");
	const [isLoading, setIsLoading] = useState({
		submit: false,
	});
	const formHeader = useMemo(() => {
		const title = t("edit.head_title");
		const description = t("edit.description");

		return { title, description };
	}, [t]);
	const formSchema = useMemo(
		() =>
			z.object({
				name: z
					.string()
					.min(3, { message: t("edit.validation.brand_name_required") }),
				description: z.string(),
				code: z
					.string()
					.min(1, { message: t("edit.validation.brand_code_required") })
					.max(3, { message: t("edit.validation.brand_code_max") }),
				active: z.boolean(),
				locations: z
					.array(
						z.object({
							id: z.union([z.string(), z.number()]),
							city: z.string(),
							active: z.boolean(),
						}),
					)
					.nullish(),
				cities: z.array(z.string()).nullish(),
				packages: z.array(
					z
						.object({
							id: z.union([z.string(), z.number()]).optional(),
							active: z.boolean(),
							isPublic: z.boolean(),
							name: z.string().min(3, {
								message: t("edit.validation.package_name_required"),
							}),
							currency: z
								.string()
								.min(3, { message: t("edit.validation.currency_required") }),
							numberOfVisit: z.coerce
								.number({ message: t("edit.validation.visit_numeric") })
								.min(1, { message: t("edit.validation.visit_min") })
								.nonnegative({ message: t("edit.validation.not_negative") }),
							numberOfFreeVisit: z.coerce
								.number({ message: t("edit.validation.free_visit_numeric") })
								.nonnegative({ message: t("edit.validation.not_negative") })
								.default(0),
							pricePerVisit: z.coerce
								.number({ message: t("edit.validation.price_numeric") })
								.nonnegative({ message: t("edit.validation.not_negative") }),
							discount: z.coerce
								.number({ message: t("edit.validation.discount_numeric") })
								.nonnegative({ message: t("edit.validation.not_negative") })
								.optional(),
							feePerVisit: z.coerce
								.number({ message: t("edit.validation.fee_numeric") })
								.nonnegative({ message: t("edit.validation.not_negative") }),
							// an emptied fee must not save as 0: z.coerce turns "" into 0, which waives the fee
							serviceFee: z.preprocess(
								(value) => (value === "" ? undefined : value),
								z.coerce
									.number({ message: t("edit.validation.service_fee_numeric") })
									.nonnegative({ message: t("edit.validation.not_negative") }),
							),
							adjustmentServiceFee: z.preprocess(
								(value) => (value === "" ? undefined : value),
								z.coerce
									.number({
										message: t(
											"edit.validation.adjustment_service_fee_numeric",
										),
									})
									.nonnegative({ message: t("edit.validation.not_negative") }),
							),
							jsonDesc: z.record(z.unknown()).nullable().default(null),
						})
						.refine((data) => data.numberOfFreeVisit < data.numberOfVisit, {
							message: t("edit.validation.free_visit_less"),
							path: ["numberOfFreeVisit"],
						}),
				),
			}),
		[t],
	);
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: service?.name?.replaceAll("_", " ") || "",
			description: service?.description || "",
			code: service?.code || "",
			active: !!service?.active,
			locations:
				service?.locations?.map(({ id, city, active }) => ({
					id,
					city,
					active,
				})) || [],
			cities: service?.locations?.map((location) => location.city) || [],
			packages:
				service?.packages?.list?.map(
					({
						id,
						active,
						isPublic,
						name,
						currency,
						numberOfVisit,
						numberOfFreeVisit,
						pricePerVisit,
						feePerVisit,
						discount,
						serviceFee,
						adjustmentServiceFee,
						jsonDesc,
					}) => ({
						id,
						active: !!active,
						isPublic: !!isPublic,
						name,
						currency,
						numberOfVisit,
						numberOfFreeVisit: numberOfFreeVisit || 0,
						pricePerVisit: Number(pricePerVisit),
						feePerVisit: Number(feePerVisit),
						discount: Number(discount),
						serviceFee: Number(serviceFee ?? 0),
						adjustmentServiceFee: Number(adjustmentServiceFee ?? 5000),
						jsonDesc: (jsonDesc ?? null) as Record<string, unknown> | null,
					}),
				) || [],
		},
		mode: "onBlur",
	});
	function onSubmit(values: z.infer<typeof formSchema>) {
		const baseURL =
			globalProps.adminPortal.router.adminPortal.serviceManagement.index;
		const routeURL = `${baseURL}/${service?.id}`;

		const originalJsonDescs = values.packages?.map((p) => p.jsonDesc) ?? [];

		const payload = deepTransformKeysToSnakeCase({
			service: {
				name: values.name.toUpperCase(),
				description: values.description,
				code: values.code.toUpperCase(),
				active: values.active,
				locations: values?.locations?.length ? values.locations : null,
				packages: values?.packages?.length ? values.packages : null,
			},
		});

		payload.service.packages?.forEach(
			(pkg: Record<string, unknown>, i: number) => {
				pkg.json_desc = originalJsonDescs[i];
			},
		);

		router.put(
			routeURL,
			payload as unknown as Parameters<typeof router.put>[1],
			{
				preserveScroll: true,
				preserveState: true,
				onStart: () => {
					setIsLoading((prev) => ({ ...prev, submit: true }));
				},
				onFinish: () => {
					setIsLoading((prev) => ({ ...prev, submit: false }));
				},
			},
		);
		console.log(`Brand with name ${values.name} successfully updated...`);
	}

	// not memoized on formState.errors: setError mutates that object in place, so a memo
	// never saw the server's errors (e.g. a package that cannot be deleted)
	const errorMessages = (() => {
		const msgs: string[] = [];
		const collect = (obj: unknown) => {
			if (!obj || typeof obj !== "object") return;
			if (
				"message" in obj &&
				typeof (obj as Record<string, unknown>).message === "string"
			) {
				msgs.push((obj as Record<string, unknown>).message as string);
				return;
			}
			for (const v of Object.values(obj as object)) {
				collect(v);
			}
		};
		collect(form.formState.errors);
		return msgs;
	})();

	// form type field array for locations
	const locationsForm = useFieldArray({
		control: form.control,
		name: "locations",
	});
	const isActiveAllCities = useMemo(
		() => !!locationsForm?.fields?.every((location) => location.active),
		[locationsForm.fields],
	);

	// form type field array for packages
	const [selectedPackageIndex, setSelectedPackageIndex] = useState(0);
	const packagesForm = useFieldArray({
		control: form.control,
		name: "packages",
		rules: {
			required: true,
			minLength: 1,
		},
	});

	useEffect(() => {
		setSelectedPackageIndex((prev) => {
			const len = packagesForm.fields.length;
			if (len === 0) return prev;
			if (prev >= len) return len - 1;
			return prev;
		});
	}, [packagesForm.fields.length]);

	// watch the cities selected data
	const watchCities = useWatch({ control: form.control, name: "cities" });
	useEffect(() => {
		const cities = watchCities;
		const locationSelected =
			locations
				?.filter((location) => cities?.includes(location.city))
				?.map((location) => ({
					id: location.id,
					city: location.city,
					active: !!locationsForm?.fields?.find(
						(field) => field.city === location.city,
					)?.active,
				})) || [];

		form.setValue("locations", locationSelected);
	}, [watchCities, locations, locationsForm?.fields?.find, form.setValue]);

	// * side-effect for server validation
	useEffect(() => {
		if (!globalProps?.errors) return;

		const errorEntries = Object.entries(
			globalProps.errors as Record<string, unknown>,
		).filter(([key]) => key !== "fullMessages");

		for (const [key, value] of errorEntries) {
			form.setError(key as any, {
				type: "custom",
				message: Array.isArray(value) ? value.join(", ") : String(value),
			});
		}
	}, [globalProps.errors, form.setError]);

	return (
		<>
			<Head title={formHeader.title} />

			<FormPageContainer>
				<section>
					<FormPageHeader
						title={formHeader.title}
						description={formHeader.description}
					/>

					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
							<Tabs
								value={activeTab}
								onValueChange={setActiveTab}
								className="w-full"
							>
								<TabsList className="w-full justify-start mb-4">
									<TabsTrigger value="brand-info">
										{t("edit.tab.brand_info")}
									</TabsTrigger>
									<TabsTrigger value="locations">
										{t("edit.tab.locations")}
									</TabsTrigger>
									<TabsTrigger value="packages">
										{t("edit.tab.packages")}
									</TabsTrigger>
								</TabsList>

								<TabsContent value="brand-info">
									<div className="grid gap-4 w-full lg:w-4/12">
										<div className="space-y-1.5">
											<p className="font-semibold leading-none tracking-tighter">
												{t("edit.brand.title")}
											</p>

											<p className="text-sm text-muted-foreground">
												{t("edit.brand.description")}
											</p>
										</div>

										<Separator />

										<FormField
											control={form.control}
											name="name"
											render={({ field }) => (
												<FormItem>
													<FormLabel>{t("edit.brand.name")}</FormLabel>
													<FormControl>
														<Input
															{...field}
															autoFocus
															type="text"
															placeholder={t("edit.brand.name_placeholder")}
															className="bg-input shadow-inner"
														/>
													</FormControl>

													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="code"
											render={({ field }) => (
												<FormItem>
													<FormLabel>{t("edit.brand.code")}</FormLabel>
													<FormControl>
														<Input
															{...field}
															type="text"
															placeholder={t("edit.brand.code_placeholder")}
															className="w-[35%] bg-input shadow-inner"
														/>
													</FormControl>

													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="active"
											render={({ field }) => {
												const statuses = [
													{
														value: "active",
														label: t("edit.brand.active"),
														color: "text-emerald-600",
													},
													{
														value: "inactive",
														label: t("edit.brand.inactive"),
														color: "text-destructive",
													},
												];

												return (
													<FormItem>
														<FormLabel>{t("edit.brand.status")}</FormLabel>
														<Select
															onValueChange={(value) => {
																field.onChange(value === "active");
															}}
															defaultValue={field.value ? "active" : "inactive"}
														>
															<FormControl>
																<SelectTrigger className="w-[50%] bg-input shadow-inner">
																	<SelectValue
																		placeholder={t(
																			"edit.brand.status_placeholder",
																		)}
																	/>
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{statuses.map((status) => (
																	<SelectItem
																		key={status.value}
																		value={status.value}
																	>
																		<span className="flex items-baseline gap-2">
																			<Dot
																				className={status.color}
																				width={10}
																				height={10}
																				strokeWidth={20}
																			/>
																			<span className="truncate">
																				{status.label}
																			</span>
																		</span>
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												);
											}}
										/>

										<FormField
											control={form.control}
											name="description"
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														{t("edit.brand.description_label")}{" "}
														<span className="text-sm italic font-light">
															{t("edit.brand.optional")}
														</span>
													</FormLabel>
													<FormControl>
														<Textarea
															{...field}
															placeholder={t(
																"edit.brand.description_placeholder",
															)}
															className="field-sizing-content max-h-29.5 min-h-0 resize-none py-1.75 bg-input shadow-inner"
														/>
													</FormControl>

													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</TabsContent>

								<TabsContent value="locations">
									<div className="grid gap-4">
										<div className="space-y-1.5">
											<p className="font-semibold leading-none tracking-tighter">
												{t("edit.locations.title")}
											</p>

											<p className="text-sm text-muted-foreground">
												{t("edit.locations.description")}
											</p>
										</div>

										<Separator />

										<FormField
											control={form.control}
											name="cities"
											render={({ field }) => (
												<FormItem>
													<MultiSelector
														onValuesChange={field.onChange}
														values={field?.value || []}
														maxShows={isMobile ? 3 : 10}
													>
														<MultiSelectorTrigger>
															<MultiSelectorInput
																placeholder={t("edit.locations.placeholder")}
															/>
														</MultiSelectorTrigger>
														<MultiSelectorContent>
															<MultiSelectorList>
																{groupedLocations.map((location) => (
																	<Fragment key={location.country}>
																		<span className="px-2 text-sm font-medium text-muted-foreground">
																			{location.country}
																		</span>
																		{location.states.map((state) => (
																			<CommandGroup
																				key={state.name}
																				heading={state.name}
																			>
																				{state.cities.map((city) => (
																					<MultiSelectorItem
																						key={city.name}
																						value={city.name}
																					>
																						{city.name}
																					</MultiSelectorItem>
																				))}
																				<CommandSeparator className="mt-2" />
																			</CommandGroup>
																		))}
																	</Fragment>
																))}
															</MultiSelectorList>
														</MultiSelectorContent>
													</MultiSelector>

													<FormMessage />
												</FormItem>
											)}
										/>

										{!!locationsForm?.fields?.length && (
											<div className="flex flex-col gap-4 p-4 border rounded-md bg-background max-h-[calc(100vh-37rem)] md:max-h-[calc(100vh-32rem)]">
												<div className="flex flex-col gap-4 pb-2 border-b">
													<FormItem className="flex flex-row items-start mb-1 space-x-3 space-y-0">
														<FormControl>
															<Checkbox
																checked={isActiveAllCities}
																onCheckedChange={(value) => {
																	const updatedLocations =
																		locationsForm.fields.map((location) => ({
																			...location,
																			active: !!value,
																		}));
																	locationsForm.replace(updatedLocations);
																}}
															/>
														</FormControl>

														<FormLabel>
															{t("edit.locations.set_active_all", {
																count: locationsForm.fields.length,
															})}
														</FormLabel>
													</FormItem>
												</div>

												<ScrollArea className="w-full h-full">
													<div className="grid gap-4 lg:gap-2 lg:grid-cols-3">
														{locationsForm?.fields?.map(
															(location, fieldIndex) => (
																<Fragment key={location.id}>
																	<div className="flex">
																		<FormField
																			control={form.control}
																			name={`locations.${fieldIndex}.active`}
																			render={({ field }) => (
																				<TooltipProvider>
																					<Tooltip>
																						<TooltipTrigger asChild>
																							<FormItem className="flex flex-row items-start space-x-3 space-y-0">
																								<FormControl>
																									<Checkbox
																										checked={field.value}
																										onCheckedChange={
																											field.onChange
																										}
																									/>
																								</FormControl>

																								<FormLabel className="leading-normal">
																									{location.city}
																								</FormLabel>
																							</FormItem>
																						</TooltipTrigger>
																						<TooltipContent side="left">
																							<p>
																								{field.value
																									? t(
																											"edit.locations.tooltip_inactive",
																										)
																									: t(
																											"edit.locations.tooltip_active",
																										)}
																							</p>
																						</TooltipContent>
																					</Tooltip>
																				</TooltipProvider>
																			)}
																		/>
																	</div>
																</Fragment>
															),
														)}
													</div>
												</ScrollArea>
											</div>
										)}
									</div>
								</TabsContent>

								<TabsContent value="packages">
									<div className="grid gap-4">
										<div className="space-y-1.5">
											<p className="font-semibold leading-none tracking-tighter">
												{t("edit.packages.title")}
											</p>

											<p className="text-sm text-muted-foreground">
												{t("edit.packages.description")}
											</p>
										</div>

										<Separator />

										{packagesForm?.fields?.length ? (
											<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-4 items-start">
												<div className="lg:col-span-2">
													<Card className="overflow-hidden bg-background">
														<CardContent className="space-y-6 pt-6">
															<div className="space-y-4">
																<p className="flex items-center gap-2 text-sm font-semibold">
																	<Info className="size-4" />
																	{t("edit.packages.information")}
																</p>

																<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
																	<FormField
																		control={form.control}
																		name={`packages.${selectedPackageIndex}.isPublic`}
																		render={({ field }) => (
																			<FormItem>
																				<FormControl>
																					<div className="relative flex w-full items-start gap-2 rounded-md bg-input shadow-inner border border-input p-3 shadow-xs outline-none has-data-[state=checked]:border-primary/50">
																						<Switch
																							className="order-1"
																							checked={field.value}
																							onCheckedChange={field.onChange}
																						/>
																						<div className="grid grow gap-1">
																							<Label className="text-sm">
																								{t("edit.packages.public")}
																							</Label>
																							<p className="text-muted-foreground text-xs">
																								{t(
																									"edit.packages.public_description",
																								)}
																							</p>
																						</div>
																					</div>
																				</FormControl>
																			</FormItem>
																		)}
																	/>

																	<FormField
																		control={form.control}
																		name={`packages.${selectedPackageIndex}.active`}
																		render={({ field }) => (
																			<FormItem>
																				<FormControl>
																					<div className="relative flex w-full items-start gap-2 rounded-md bg-input shadow-inner border border-input p-3 shadow-xs outline-none has-data-[state=checked]:border-primary/50">
																						<Switch
																							className="order-1"
																							checked={field.value}
																							onCheckedChange={field.onChange}
																						/>
																						<div className="grid grow gap-1">
																							<Label className="text-sm">
																								{t("edit.packages.active")}
																							</Label>
																							<p className="text-muted-foreground text-xs">
																								{t(
																									"edit.packages.active_description",
																								)}
																							</p>
																						</div>
																					</div>
																				</FormControl>
																			</FormItem>
																		)}
																	/>
																</div>

																<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
																	<FormField
																		control={form.control}
																		name={`packages.${selectedPackageIndex}.name`}
																		render={({ field }) => (
																			<FormItem className="col-span-full md:col-span-2">
																				<FormLabel>
																					{t("edit.packages.name")}
																				</FormLabel>
																				<FormControl>
																					<Input
																						{...field}
																						placeholder={t(
																							"edit.packages.name_placeholder",
																						)}
																						className="bg-input shadow-inner"
																					/>
																				</FormControl>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>

																	<FormField
																		control={form.control}
																		name={`packages.${selectedPackageIndex}.currency`}
																		render={({ field }) => (
																			<FormItem>
																				<FormLabel>
																					{t("edit.packages.currency")}
																				</FormLabel>
																				<FormControl>
																					<Input
																						{...field}
																						placeholder={t(
																							"edit.packages.currency_placeholder",
																						)}
																						className="uppercase bg-input shadow-inner"
																					/>
																				</FormControl>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>
																</div>
															</div>

															<Separator />

															{/* Section 2: Visits */}
															<div className="space-y-4">
																<p className="flex items-center gap-2 text-sm font-semibold">
																	<Tag className="size-4" />
																	{t("edit.packages.visit_configuration")}
																</p>

																<div className="grid grid-cols-1 md:grid-cols-5 gap-4">
																	<FormField
																		control={form.control}
																		name={`packages.${selectedPackageIndex}.numberOfVisit`}
																		render={({ field }) => (
																			<FormItem>
																				<FormLabel>
																					{t("edit.packages.total_visits")}
																				</FormLabel>
																				<FormControl>
																					<Input
																						{...field}
																						type="number"
																						min={0}
																						className="bg-input shadow-inner [&::-webkit-inner-spin-button]:appearance-none"
																					/>
																				</FormControl>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>

																	<FormField
																		control={form.control}
																		name={`packages.${selectedPackageIndex}.numberOfFreeVisit`}
																		render={({ field }) => (
																			<FormItem>
																				<FormLabel>
																					{t("edit.packages.free_visits")}
																				</FormLabel>
																				<FormControl>
																					<Input
																						{...field}
																						type="number"
																						min={0}
																						className="bg-input shadow-inner [&::-webkit-inner-spin-button]:appearance-none"
																					/>
																				</FormControl>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>
																</div>
															</div>

															<Separator />

															{/* Section 3: Pricing */}
															<div className="space-y-4">
																<p className="flex items-center gap-2 text-sm font-semibold">
																	<CreditCard className="size-4" />
																	{t("edit.packages.pricing")}
																</p>

																<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
																	<FormField
																		control={form.control}
																		name={`packages.${selectedPackageIndex}.pricePerVisit`}
																		render={({ field }) => (
																			<FormItem>
																				<FormLabel>
																					{t("edit.packages.price_per_visit")}
																				</FormLabel>
																				<FormControl>
																					<InputExtended
																						{...field}
																						type="number"
																						min={0}
																						className="bg-input shadow-inner [&::-webkit-inner-spin-button]:appearance-none"
																					>
																						<InputExtended.Group>
																							<InputExtended.RightIcon>
																								<span className="text-xs text-muted-foreground">
																									{
																										packagesForm.fields[
																											selectedPackageIndex
																										]?.currency
																									}
																								</span>
																							</InputExtended.RightIcon>
																						</InputExtended.Group>
																					</InputExtended>
																				</FormControl>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>

																	<FormField
																		control={form.control}
																		name={`packages.${selectedPackageIndex}.discount`}
																		render={({ field }) => (
																			<FormItem>
																				<FormLabel>
																					{t("edit.packages.total_discount")}
																				</FormLabel>
																				<FormControl>
																					<InputExtended
																						{...field}
																						type="number"
																						min={0}
																						className="bg-input shadow-inner [&::-webkit-inner-spin-button]:appearance-none"
																					>
																						<InputExtended.Group>
																							<InputExtended.RightIcon>
																								<span className="text-xs text-muted-foreground">
																									{
																										packagesForm.fields[
																											selectedPackageIndex
																										]?.currency
																									}
																								</span>
																							</InputExtended.RightIcon>
																						</InputExtended.Group>
																					</InputExtended>
																				</FormControl>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>

																	<FormField
																		control={form.control}
																		name={`packages.${selectedPackageIndex}.feePerVisit`}
																		render={({ field }) => (
																			<FormItem>
																				<FormLabel>
																					{t("edit.packages.fee_per_visit")}
																				</FormLabel>
																				<FormControl>
																					<InputExtended
																						{...field}
																						type="number"
																						min={0}
																						className="bg-input shadow-inner [&::-webkit-inner-spin-button]:appearance-none"
																					>
																						<InputExtended.Group>
																							<InputExtended.RightIcon>
																								<span className="text-xs text-muted-foreground">
																									{
																										packagesForm.fields[
																											selectedPackageIndex
																										]?.currency
																									}
																								</span>
																							</InputExtended.RightIcon>
																						</InputExtended.Group>
																					</InputExtended>
																				</FormControl>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>
																</div>

																{serviceFeeEnabled && (
																	<>
																		<p className="text-xs font-medium text-muted-foreground">
																			{t("edit.packages.patient_fees_heading")}
																		</p>
																		<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
																			{(
																				[
																					["serviceFee", "service_fee"],
																					[
																						"adjustmentServiceFee",
																						"adjustment_service_fee",
																					],
																				] as const
																			).map(([fieldName, key]) => (
																				<FormField
																					key={fieldName}
																					control={form.control}
																					name={`packages.${selectedPackageIndex}.${fieldName}`}
																					render={({ field }) => (
																						<FormItem>
																							<FormLabel>
																								{t(`edit.packages.${key}`)}
																							</FormLabel>
																							<FormControl>
																								<InputExtended
																									{...field}
																									type="number"
																									min={0}
																									className="bg-input shadow-inner [&::-webkit-inner-spin-button]:appearance-none"
																								>
																									<InputExtended.Group>
																										<InputExtended.RightIcon>
																											<span className="text-xs text-muted-foreground">
																												{
																													packagesForm.fields[
																														selectedPackageIndex
																													]?.currency
																												}
																											</span>
																										</InputExtended.RightIcon>
																									</InputExtended.Group>
																								</InputExtended>
																							</FormControl>
																							<FormDescription>
																								{t(`edit.packages.${key}_help`)}
																							</FormDescription>
																							<FormMessage />
																						</FormItem>
																					)}
																				/>
																			))}
																		</div>
																	</>
																)}
															</div>

															<Separator />

															{/* Section 4: Desc */}
															<div className="space-y-4">
																<p className="flex items-center gap-2 text-sm font-semibold">
																	<FileText className="size-4" />
																	{t("edit.packages.additional_details")}
																</p>

																<PackageDescriptionEditor
																	fieldIndex={selectedPackageIndex}
																	form={form}
																/>
															</div>

															<PackageSummaryCard
																fieldIndex={selectedPackageIndex}
																form={form}
																currency={
																	packagesForm.fields[selectedPackageIndex]
																		?.currency
																}
															/>
														</CardContent>
													</Card>
												</div>

												<div className="order-first lg:order-last lg:col-span-1">
													<div className="sticky top-4 space-y-3 bg-background">
														<ScrollArea className="max-h-[24dvh] md:max-h-[43dvh] overflow-y-auto rounded-md border">
															<div className="p-2 flex items-center justify-between">
																<p className="flex items-center gap-2 text-sm font-semibold">
																	<PackageIcon className="size-4" />
																	{t("edit.packages.list")}
																</p>

																<Button
																	key="add-pkg-list"
																	type="button"
																	size="sm"
																	onClick={(e) => {
																		e.preventDefault();
																		e.stopPropagation();
																		const len = packagesForm.fields.length;
																		if (len > 0) {
																			const lastPkg = form.getValues(
																				`packages.${len - 1}`,
																			);
																			if (
																				lastPkg &&
																				(!lastPkg.name ||
																					lastPkg.name.trim() === "" ||
																					lastPkg.name === "New Package" ||
																					!lastPkg.currency ||
																					lastPkg.currency.trim() === "")
																			) {
																				toast.error(
																					t(
																						"edit.packages.incomplete_toast.title",
																					),
																					{
																						description: t(
																							"edit.packages.incomplete_toast.description",
																							{ name: lastPkg.name ?? "" },
																						),
																					},
																				);
																				return;
																			}
																		}

																		const name = "New Package";
																		packagesForm?.append({
																			name,
																			currency: "",
																			active: false,
																			isPublic: true,
																			numberOfVisit: 1,
																			numberOfFreeVisit: 0,
																			pricePerVisit: 0,
																			feePerVisit: 0,
																			discount: 0,

																			serviceFee: 0,
																			adjustmentServiceFee: 5000,
																			jsonDesc: null,
																		});
																		setSelectedPackageIndex(
																			packagesForm.fields.length - 1,
																		);
																	}}
																>
																	<Plus className="size-3.5" />
																	{t("edit.packages.add")}
																</Button>
															</div>

															<Separator />

															<div className="space-y-0.5 p-2">
																{packagesForm.fields.map((pkg, idx) => (
																	// biome-ignore lint/a11y/useSemanticElements: button cannot contain another button (AlertDialog trigger)
																	<div
																		role="button"
																		tabIndex={0}
																		key={pkg.id}
																		className={
																			"flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors w-full text-left " +
																			(selectedPackageIndex === idx
																				? "bg-primary/10 text-primary ring-1 ring-primary/20"
																				: "hover:bg-muted text-muted-foreground hover:text-foreground")
																		}
																		onClick={() => setSelectedPackageIndex(idx)}
																		onKeyDown={(e) => {
																			if (e.key === "Enter" || e.key === " ") {
																				e.preventDefault();
																				setSelectedPackageIndex(idx);
																			}
																		}}
																	>
																		<div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary shrink-0">
																			{idx + 1}
																		</div>
																		<span className="text-sm truncate flex-1 font-medium">
																			{pkg.name || t("edit.packages.untitled")}
																		</span>
																		<AlertDialog>
																			<AlertDialogTrigger asChild>
																				<Button
																					variant="ghost"
																					size="icon"
																					className="size-6 shrink-0 text-muted-foreground hover:text-destructive"
																					onClick={(e) => e.stopPropagation()}
																				>
																					<Trash2 className="size-3" />
																				</Button>
																			</AlertDialogTrigger>
																			<AlertDialogContent>
																				<AlertDialogHeader>
																					<AlertDialogTitle>
																						{t(
																							"edit.packages.delete_dialog.title",
																						)}
																					</AlertDialogTitle>
																					<AlertDialogDescription>
																						{t(
																							"edit.packages.delete_dialog.description",
																						)}
																					</AlertDialogDescription>
																				</AlertDialogHeader>
																				<AlertDialogFooter>
																					<AlertDialogCancel>
																						{t(
																							"edit.packages.delete_dialog.cancel",
																						)}
																					</AlertDialogCancel>
																					<Button
																						variant="destructive"
																						onClick={(event) => {
																							event.preventDefault();
																							event.stopPropagation();
																							packagesForm?.remove(idx);
																							setTimeout(() => {
																								form.trigger("packages");
																							}, 0);
																						}}
																					>
																						{t(
																							"edit.packages.delete_dialog.confirm",
																						)}
																					</Button>
																				</AlertDialogFooter>
																			</AlertDialogContent>
																		</AlertDialog>
																	</div>
																))}
															</div>
														</ScrollArea>
													</div>
												</div>
											</div>
										) : (
											<div className="flex flex-col items-center gap-4 py-12 text-center">
												<p className="text-sm text-muted-foreground">
													{t("edit.packages.empty")}
												</p>
												<Button
													key="add-pkg-empty"
													type="button"
													variant="outline"
													onClick={(e) => {
														e.preventDefault();
														e.stopPropagation();

														const len = packagesForm.fields.length;
														if (len > 0) {
															const lastPkg = form.getValues(
																`packages.${len - 1}`,
															);
															if (
																lastPkg &&
																(!lastPkg.name ||
																	lastPkg.name.trim() === "" ||
																	lastPkg.name === "New Package" ||
																	!lastPkg.currency ||
																	lastPkg.currency.trim() === "")
															) {
																return;
															}
														}

														const name = "New Package";
														packagesForm?.append({
															name,
															currency: "",
															active: false,
															isPublic: true,
															numberOfVisit: 1,
															numberOfFreeVisit: 0,
															pricePerVisit: 0,
															feePerVisit: 0,
															discount: 0,

															serviceFee: 0,
															adjustmentServiceFee: 5000,
															jsonDesc: null,
														});
														setSelectedPackageIndex(0);
													}}
												>
													<Plus className="size-4" />
													{t("edit.packages.add_package")}
												</Button>
											</div>
										)}
									</div>
								</TabsContent>
							</Tabs>

							{errorMessages.length > 0 && (
								<Alert variant="destructive">
									<AlertTitle>{t("edit.validation_error")}</AlertTitle>
									<AlertDescription className="whitespace-pre-line">
										{errorMessages.join("\n")}
									</AlertDescription>
								</Alert>
							)}

							<div className="gap-4 lg:gap-2 w-full flex flex-col md:flex-row lg:col-span-full md:justify-between order-last">
								<Button
									type="button"
									variant="outline"
									className="w-full shadow-none lg:w-auto"
									onClick={goBackHandler}
								>
									{t("edit.cancel")}
								</Button>

								<Button
									type="submit"
									disabled={isLoading.submit}
									className="order-first w-full shadow-none md:order-last lg:w-auto"
								>
									{isLoading.submit ? (
										<>
											<LoaderIcon className="animate-spin" />
											<span>{t("edit.updating")}</span>
										</>
									) : (
										<span>{t("edit.save")}</span>
									)}
								</Button>
							</div>
						</form>
					</Form>
				</section>
			</FormPageContainer>
		</>
	);
}
